import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Height of the sticky site header; a card parks just below it.
const STACK_TOP = 76;
// Vertical stagger so the top edge of buried cards stays visible.
const STACK_STEP = 12;
// Breathing room under a card that is taller than the viewport.
const BOTTOM_GAP = 16;

/**
 * One panel in the scroll-stack.
 *
 * Cards park under the header and the next one slides over them. A card that
 * is taller than the viewport would otherwise have its lower half permanently
 * buried, so those park against the *bottom* of the viewport instead — they
 * scroll up until fully read, then hold while the next card arrives.
 */
export const StackCard = ({ index = 0, isLast = false, children, className = '' }) => {
  const panelRef = useRef(null);
  const [top, setTop] = useState(STACK_TOP + index * STACK_STEP);
  const [pinned, setPinned] = useState(true);

  const measure = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const base = STACK_TOP + index * STACK_STEP;
    const fitted = window.innerHeight - panel.offsetHeight - BOTTOM_GAP;
    // Bottom-parking only works while the overshoot is small. A card much
    // taller than the viewport would need a negative offset deep enough to
    // hold its own heading off-screen, so that card scrolls with the page
    // instead of pinning at all.
    if (fitted < STACK_TOP - panel.offsetHeight / 2) {
      setPinned(false);
      return;
    }
    setPinned(true);
    setTop(Math.min(base, fitted));
  }, [index]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);

    let observer;
    if (typeof ResizeObserver !== 'undefined' && panelRef.current) {
      observer = new ResizeObserver(measure);
      observer.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [measure]);

  return (
    <div
      className={pinned ? 'sticky' : 'relative'}
      style={pinned ? { top, zIndex: 10 + index } : { zIndex: 10 + index }}
    >
      <div
        ref={panelRef}
        className={`relative mx-auto w-full max-w-[1500px] rounded-t-[60px] border border-black/15 bg-white px-4 pb-10 pt-9 shadow-[0_-6px_20px_rgba(0,0,0,0.10)] md:rounded-t-[90px] md:px-10 md:pb-12 md:pt-10 ${className}`}
      >
        {children}

        {/* Skirt: continues the card below its content so the next card's
            rounded corners always meet white instead of the page ground.
            Absolutely placed, so it costs no scroll distance. */}
        {!isLast && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-[-1px] top-full h-[140px] border-x border-black/15 bg-white"
          />
        )}
      </div>
    </div>
  );
};

export const StackCardTitle = ({ children }) => (
  <h2 className="text-center text-[26px] font-bold leading-tight text-black md:text-[38px]">
    {children}
  </h2>
);

/**
 * The grey topic tile used throughout the homepage stack: kicker title,
 * square thumbnail, body copy, and a full-width "Learn more" action.
 */
export const TopicCard = ({ title, image, imageAlt, to, children }) => (
  <div className="flex flex-col rounded-[36px] bg-[#D9D9D9] px-5 pb-4 pt-5 md:px-7 md:pt-6">
    <h3 className="text-[15px] font-bold text-black md:text-[17px]">/ {title}</h3>

    <div className="mt-3 flex flex-1 flex-col gap-4 sm:flex-row sm:gap-5">
      <img
        src={image}
        alt={imageAlt}
        className="h-[170px] w-[150px] flex-shrink-0 rounded-[22px] object-cover shadow-md sm:h-[195px] sm:w-[170px]"
      />
      <div className="space-y-2 text-[14px] leading-snug text-black md:text-[15px]">
        {children}
      </div>
    </div>

    <Link to={to} className="group mt-4 block">
      <span className="flex items-end justify-between text-base text-black md:text-lg">
        <span>Learn more</span>
        <span aria-hidden="true">&rarr;</span>
      </span>
      <span className="mt-1 block h-px w-full bg-black/60 transition-colors group-hover:bg-black" />
    </Link>
  </div>
);

export const TopicGrid = ({ children }) => (
  <div className="mt-6 grid grid-cols-1 gap-4 md:mt-7 lg:grid-cols-2 lg:gap-6">
    {children}
  </div>
);
